import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

interface ImportError {
  record: string;
  errors: string[];
}

interface ImportResult {
  message: string;
  imported: number;
  total: number;
  file: string;
  importType: string;
  errors?: ImportError[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  result: ImportResult | null;
}

const ImportStatusModal: React.FC<Props> = ({ open, onClose, result }) => {
  if (!result) return null;

  const hasErrors = result.errors && result.errors.length > 0;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Import Status</DialogTitle>
      <DialogContent sx={{ padding: '2rem' }}>
        <Box sx={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          {hasErrors ? (
            <ErrorOutlineIcon sx={{ fontSize: 60, color: 'error.main' }} />
          ) : (
            <CheckCircleOutlineIcon sx={{ fontSize: 60, color: 'success.main' }} />
          )}
        </Box>
        <Typography variant="h5" gutterBottom sx={{ textAlign: 'center' }}>
          {result.message}
        </Typography>
        <Box mt={2} mb={3} display="flex" justifyContent="center" gap={2}>
          <Chip label={`File: ${result.file}`} />
          <Chip label={`Type: ${result.importType}`} />
        </Box>

        {hasErrors && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Error Details
            </Typography>
            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Record</TableCell>
                    <TableCell>Errors</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.errors?.map((error, index) => (
                    <TableRow key={index}>
                      <TableCell sx={{ verticalAlign: 'top' }}>
                        <Typography variant="body2">{error.record}</Typography>
                      </TableCell>
                      <TableCell>
                        <List dense disablePadding>
                          {error.errors.map((errMsg, errIndex) => (
                            <ListItem key={errIndex} disableGutters>
                              <ListItemText primary={`- ${errMsg}`} />
                            </ListItem>
                          ))}
                        </List>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', paddingBottom: '1.5rem' }}>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportStatusModal;
